import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { ArrowDown, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { COMPANY_STATS } from '../../data/stats';

// HERO BACKGROUND: client-supplied OMC water-tower drone footage. The hero
// alternates between three OMC landmark clips (Trilith Studios first, then
// SpaceX, then a composite tank-foundation build at golden hour).
const HERO_VIDEOS = [
  {
    src: '/brand/hero-trilith.mp4?v=2',
    poster: '/brand/hero-trilith-poster.jpg',
    caption: 'Trilith Studios · Foundation by OMC',
    alt: 'Aerial view of the Trilith Studios water tower at dusk — foundation built by OMC',
  },
  {
    src: '/brand/hero-omc-hd.mp4?v=3',
    poster: '/brand/hero-omc-poster.jpg?v=3',
    caption: 'SpaceX Water Tower · Foundation by OMC',
    alt: 'SpaceX Falcon 9 lifting off — the SpaceX water tower in the foreground sits atop an OMC-built reinforced concrete foundation',
    maxSeconds: 13,
  },
  {
    src: '/brand/hero-foundation.mp4?v=1',
    poster: '/brand/hero-foundation-poster.jpg?v=1',
    caption: 'Composite Tank Foundation · Built by OMC',
    alt: 'Golden-hour aerial of an OMC composite elevated tank foundation — circular ring formwork and vertical reinforcing steel set in an open excavation as crews and an excavator work the site',
  },
];

export default function HeroSection() {
  const videoRef = useRef(null);
  const [, setVideoLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [index, setIndex] = useState(0);
  const current = HERO_VIDEOS[index];
  const navigate = useNavigate();

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (event) => setPrefersReducedMotion(event.matches);
    setPrefersReducedMotion(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Cap clips that define a maxSeconds (e.g. the long SpaceX launch) so they
  // advance after 10-15s instead of playing their full runtime.
  useEffect(() => {
    if (prefersReducedMotion) return undefined;
    const max = HERO_VIDEOS[index].maxSeconds;
    if (!max) return undefined;
    const t = setTimeout(() => setIndex((i) => (i + 1) % HERO_VIDEOS.length), max * 1000);
    return () => clearTimeout(t);
  }, [index, prefersReducedMotion]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="top" className="hero-video-container" data-testid="hero-section">
      {/* Background: alternating OMC water-tower drone clips (SpaceX ↔ Trilith) */}
      {!prefersReducedMotion ? (
        <motion.video
          key={index}
          ref={videoRef}
          autoPlay
          muted
          playsInline
          poster={current.poster}
          onLoadedData={() => setVideoLoaded(true)}
          onEnded={() => setIndex((i) => (i + 1) % HERO_VIDEOS.length)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
          aria-hidden="true"
        >
          <source src={current.src} type="video/mp4" />
        </motion.video>
      ) : (
        <img
          src={current.poster}
          alt={current.alt}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-background-image"
        />
      )}

      <div className="hero-overlay" />
      <div className="noise-overlay" />

      {/* Photo caption — discreet attribution to the SpaceX launch complex */}
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.9 }}
        className="absolute top-24 sm:top-28 right-4 sm:right-6 lg:right-10 z-[2] flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-coal/55 backdrop-blur-md"
        data-testid="hero-photo-caption"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--water-blue-2))] shadow-[0_0_8px_hsl(var(--water-blue-2))]" />
        <AnimatePresence mode="wait">
          <motion.span
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/85"
            data-testid="hero-caption-text"
          >
            {current.caption}
          </motion.span>
        </AnimatePresence>
      </motion.div>

      {/* Content — lower-third editorial layout */}
      <div className="hero-content flex flex-col justify-end min-h-[92vh] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-24 lg:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
          className="max-w-4xl"
          data-testid="hero-content"
        >
          {/* Kicker chip row — single elegant pill */}
          <div className="flex flex-wrap items-center gap-3 mb-7" data-testid="hero-kicker-row">
            <span className="kicker-chip" data-testid="hero-kicker-chip-hq">
              <MapPin className="w-3 h-3 opacity-80" /> Conway, AR · Nationwide · Est. 1999
            </span>
          </div>

          {/* Editorial headline — serif display */}
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.22, 0.61, 0.36, 1] }}
            className="editorial-headline text-white text-[2.5rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] mb-6"
            data-testid="hero-headline"
          >
            The Foundation
            <span className="italic font-bold text-[hsl(var(--energy-green-2))]" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.8)' }}> behind </span>
            America&rsquo;s Critical Infrastructure.
          </motion.h1>

          {/* Subhead */}
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.24 }}
            className="text-base sm:text-lg lg:text-xl text-white/80 font-body leading-[1.6] mb-10 max-w-2xl"
            data-testid="hero-subhead"
          >
            Building the foundations, structures, and systems behind critical
            <span className="font-semibold text-white"> water</span>,
            <span className="font-semibold text-white"> industrial</span>, and
            <span className="font-semibold text-white"> energy</span>
            {' '}infrastructure since 1999.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.36 }}
            className="flex flex-wrap gap-3 mb-12"
          >
            <Button
              size="lg"
              className="btn-lift text-sm tracking-wide px-7 py-6 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-elev-1"
              onClick={() => navigate('/contact')}
              data-testid="hero-contact-us-button"
            >
              Contact Us
            </Button>
          </motion.div>

          {/* Proof strip — 4 stats matching the corporate ozarkmtnconstructionco.com style */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="proof-strip py-6 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10 max-w-3xl"
            data-testid="hero-proof-strip"
          >
            {COMPANY_STATS.map((stat) => (
              <div key={stat.label} className="flex flex-col" data-testid={`hero-stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="font-display leading-none mb-2 flex items-start" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>
                  <span className="text-[2.75rem] sm:text-5xl text-white font-semibold tracking-tight">
                    {stat.value}
                  </span>
                  {stat.plus ? (
                    <span className="text-3xl sm:text-4xl text-white font-semibold ml-0.5 mt-1">
                      {stat.plus}
                    </span>
                  ) : null}
                </div>
                <div className="font-mono text-[9px] sm:text-[10px] tracking-[0.1em] whitespace-nowrap uppercase text-white/70">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.button
          onClick={() => scrollTo('heritage')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 0.6 }}
          className="absolute bottom-6 right-6 sm:right-10 flex items-center gap-2 text-white/55 hover:text-white transition-colors"
          data-testid="hero-scroll-down"
        >
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase">Scroll</span>
          <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
        </motion.button>
      </div>
    </section>
  );
}
